import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Smartphone,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import ClipConverterComponent from '../components/tools/ClipConverter';

const ClipConverterPage: React.FC = () => {
  const { currentUser } = useAuth();

  return (
    <>
      <Helmet>
        <title>Free Clip Converter - ClipThis</title>
        <meta name="description" content="Free clip converter. Convert your videos to vertical format for TikTok, Instagram Reels, YouTube Shorts. Fast, secure, and no registration required." />
        <meta name="keywords" content="clip converter, video converter, vertical video, TikTok format, Instagram Reels, YouTube Shorts" />
      </Helmet>
      <BreadcrumbSchema items={[
        { name: 'Clip Converter', path: '/converter' }
      ]} />

      <div className="min-h-screen relative overflow-x-hidden" style={{ minHeight: 'calc(100vh - 64px)' }}>
        {/* Subtle animated background */}
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
        
        <div className="relative z-10 px-6 sm:px-8 pt-4 animate-fade-up">
          {/* Back Button */}
          <Link 
            to={currentUser ? "/dashboard" : "/"}
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span>Back to {currentUser ? "Dashboard" : "Home"}</span>
          </Link>
        
        {/* Hero Section */}
        <div className="py-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-8 h-8 text-white" />
                </div>
              </div>
              <h1 className="text-5xl font-display font-bold tracking-tight mb-4">
                Clip Converter
              </h1>
              <p className="text-xl text-primary-600 max-w-3xl mx-auto">
                Convert your videos to vertical format for TikTok, Instagram Reels, and YouTube Shorts instantly.
              </p>
              
              {/* Usage clarification */}
              <div className="mt-6 max-w-4xl mx-auto bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">💡</span>
                  </div>
                  <div className="text-left">
                    <h3 className="text-gray-800 font-semibold mb-2">When to use Clip Converter:</h3>
                    <p className="text-gray-700 text-sm mb-2">
                      <strong>Use this tool when:</strong> You already have your perfect clip but it's in landscape format and you want to convert it to vertical format for social media.
                    </p>
                    <p className="text-gray-700 text-sm">
                      <strong>Have a full video or YouTube URL?</strong> Use the <Link to="/upload" className="underline font-medium hover:text-red-600 transition-colors">Upload section</Link> instead to create clips from longer content.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Clip Converter Content */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-primary-100 shadow-sm">
            <ClipConverterComponent />
          </div>
        </div>
        </div>

      </div>
    </>
  );
};

export default ClipConverterPage;
